import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { checkForErrors, getCustomerPypes } from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import {
  DEFAULT_ERROR_HANDLING_VALUES,
  ErrorHandlingData,
  MS_PER_SECOND,
} from './load-error-handling';

/**
 * Writes Timeout + Total Attempts (bot_retry) to every pype in the env via the
 * single bulk endpoint — one request regardless of pype count (a per-pype loop
 * would fail at scale, e.g. a project with 1000 pypes).
 *
 * Only `botRetry` is sent in `updates` (shared across all pype ids). The bulk
 * handler validates every changeset up front and fails atomically before any
 * write, so a rejected payload changes no data.
 */
export const updateErrorHandling = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<(ErrorHandlingData & { cb?: () => void }) | void> => {
  if (event.type !== 'updateErrorHandling') {
    return;
  }

  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId || !customerId) {
    throwErrorWithId('Project ID and Customer ID are required');
    return;
  }

  const { timeout, attempts, env, cb } = event.payload;

  // The pype list must come from a source that is definitely current, NOT from
  // `ctx.liveAgentPypes` alone. That slice is shared with several other flows and
  // `invalidateChannelDerivedCaches` nulls it on any channel create/delete/rename
  // — while leaving `ctx.errorHandling` set, which is what the page gates its
  // reload on. So the cache can legitimately be null here with pypes existing,
  // and reading it blindly meant the bulk went out with an empty `pypeIds`
  // (silently writing nothing, yet reporting success).
  //
  // Re-fetch only when the cache is unusable, so the common path stays free.
  const cachedPypes = ctx.liveAgentPypes;
  const hasCachedPypes = (cachedPypes?.[env]?.length ?? 0) > 0;
  const pypesByEnv = hasCachedPypes
    ? cachedPypes!
    : await getCustomerPypes({ uniqueCustomerId: customerId, projectId });

  const pypeIds = (pypesByEnv[env] ?? [])
    .map((pype) => pype.id)
    .filter((id): id is string => !!id);

  // Now that the list is known-current, an empty one really means "this env has
  // no channels", which the bulk endpoint would otherwise accept as a no-op and
  // the flow would report as a successful update.
  if (pypeIds.length === 0) {
    throwErrorWithId(`No channels in ${env} to apply error handling to`);
    return;
  }

  const bulkMutation = {
    live: urqlGql.bulkUpdatePypesLive,
    sandbox: urqlGql.bulkUpdatePypesSandbox,
  }[env];

  const response = await bulkMutation({
    projectId,
    customerId,
    bulkPypeUpdateInput: {
      pypeIds,
      updates: { botRetry: { timeout: timeout * MS_PER_SECOND, attempts } },
    },
  });
  checkForErrors(response);

  // No re-read: reflect the just-saved values for this env and keep the other
  // env's values from context (avoids a needless cross-env config fetch).
  const current = ctx.errorHandling ?? {
    live: DEFAULT_ERROR_HANDLING_VALUES,
    sandbox: DEFAULT_ERROR_HANDLING_VALUES,
  };

  return {
    errorHandling: { ...current, [env]: { timeout, attempts } },
    // `pypesByEnv`, not the context slice: on the re-fetch path this repopulates
    // the invalidated cache instead of writing an empty pair back over it.
    liveAgentPypes: pypesByEnv,
    cb,
  };
};
